import React from 'react';
import { AnimatedSection } from './AnimatedSection';
import { StarField } from './StarField';
import { XCircleIcon, AlertTriangleIcon } from 'lucide-react';
export function ProblemSection() {
  return <section className="relative bg-night-800 py-20 md:py-28 overflow-hidden" data-id="element-255">
      <StarField data-id="element-256" />

      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8" data-id="element-257">
        {/* Opening Hook */}
        <AnimatedSection data-id="element-258">
          <div className="bg-night-700/50 border border-night-600/40 rounded-2xl p-8 md:p-10 mb-16" data-id="element-259">
            <p className="font-playfair text-2xl sm:text-3xl italic text-cream-200/90 leading-relaxed mb-6" data-id="element-260">
              Son las 3:14 AM.
            </p>
            <p className="font-dm-sans text-base sm:text-lg text-cream-200/70 leading-relaxed mb-4" data-id="element-261">
              Estás sentada en la oscuridad, meciendo un cuerpo pequeño mientras
              tus párpados pesan como plomo. Sientes una mezcla de amor infinito
              y una desesperación silenciosa que nadie te advirtió.
            </p>
            <p className="font-dm-sans text-base sm:text-lg text-cream-200/70 leading-relaxed mb-4" data-id="element-262">
              Miras el reloj con miedo, sabiendo que en pocas horas debes
              levantarte a trabajar o a enfrentar otro día en neblina.
            </p>
            <p className="font-dm-sans text-lg sm:text-xl text-cream-100 leading-relaxed font-medium" data-id="element-263">
              Te preguntas por milésima vez:{' '}
              <span className="text-gold-400 italic" data-id="element-264">
                "¿Qué estoy haciendo mal?"
              </span>
            </p>
          </div>
        </AnimatedSection>

        {/* The Problem */}
        <AnimatedSection data-id="element-265">
          <div className="mb-16" data-id="element-266">
            <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-cream-100 mb-6 text-center" data-id="element-267">
              El cansancio extremo no es{' '}
              <span className="text-gold-400 italic" data-id="element-268">solo</span> falta de sueño
            </h2>
            <p className="font-dm-sans text-base sm:text-lg text-cream-200/70 leading-relaxed mb-4 text-center max-w-2xl mx-auto" data-id="element-269">
              Es una sombra que te quita la paciencia con tu pareja, te hace
              cometer errores en el trabajo y te roba la alegría de la
              maternidad.
            </p>
            <p className="font-dm-sans text-base sm:text-lg text-cream-200/70 leading-relaxed text-center max-w-2xl mx-auto" data-id="element-270">
              Tu bebé no tiene la culpa. Su cerebro simplemente está recibiendo{' '}
              <strong className="text-cream-100" data-id="element-271">
                las señales equivocadas
              </strong>
              . El calor atrapado en la habitación, las luces blancas y el ruido
              de la calle mantienen su sistema en{' '}
              <span className="text-red-400/80" data-id="element-272">alerta roja</span> cuando
              debería estar en reposo profundo.
            </p>
          </div>
        </AnimatedSection>

        {/* Divider */}
        <div className="flex items-center justify-center gap-3 mb-16" aria-hidden="true" data-id="element-273">
          <div className="h-px w-16 bg-night-600" data-id="element-274" />
          <AlertTriangleIcon className="w-5 h-5 text-gold-500/60" data-id="element-275" />
          <div className="h-px w-16 bg-night-600" data-id="element-276" />
        </div>

        {/* Why What You Tried Didn't Work */}
        <AnimatedSection data-id="element-277">
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-cream-100 mb-10 text-center" data-id="element-278">
            Lo que ya intentaste{' '}
            <span className="text-cream-200/50" data-id="element-279">(y por qué no funcionó)</span>
          </h2>
        </AnimatedSection>

        <div className="space-y-5" data-id="element-280">
          <AnimatedSection delay={0} data-id="element-281">
            <div className="flex items-start gap-4 bg-night-700/30 border border-red-400/10 rounded-xl p-5" data-id="element-282">
              <XCircleIcon className="w-6 h-6 text-red-400/70 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-283" />
              <p className="font-dm-sans text-base text-cream-200/80 leading-relaxed" data-id="element-284">
                <strong className="text-cream-100" data-id="element-285">
                  El método de "dejarlo llorar"
                </strong>{' '}
                solo eleva su cortisol, haciendo que el despertar sea más
                violento.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={80} data-id="element-286">
            <div className="flex items-start gap-4 bg-night-700/30 border border-red-400/10 rounded-xl p-5" data-id="element-287">
              <XCircleIcon className="w-6 h-6 text-red-400/70 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-288" />
              <p className="font-dm-sans text-base text-cream-200/80 leading-relaxed" data-id="element-289">
                <strong className="text-cream-100" data-id="element-290">
                  Intentar agotarlo durante el día
                </strong>{' '}
                para que "caiga rendido" solo genera sobreestimulación y más
                despertares nocturnos.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={160} data-id="element-291">
            <div className="flex items-start gap-4 bg-night-700/30 border border-red-400/10 rounded-xl p-5" data-id="element-292">
              <XCircleIcon className="w-6 h-6 text-red-400/70 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-293" />
              <p className="font-dm-sans text-base text-cream-200/80 leading-relaxed" data-id="element-294">
                <strong className="text-cream-100" data-id="element-295">
                  Darle una toma pesada antes de dormir
                </strong>{' '}
                satura su digestión en lugar de relajar su sistema nervioso.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={240} data-id="element-296">
            <div className="flex items-start gap-4 bg-night-700/30 border border-red-400/10 rounded-xl p-5" data-id="element-297">
              <XCircleIcon className="w-6 h-6 text-red-400/70 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-298" />
              <p className="font-dm-sans text-base text-cream-200/80 leading-relaxed" data-id="element-299">
                <strong className="text-cream-100" data-id="element-300">
                  Los consejos genéricos de libros extranjeros
                </strong>{' '}
                no entienden nuestra luz, nuestro clima ni nuestras rutinas
                locales.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>;
}